import { Suspense } from 'react';

import { Tab } from '@headlessui/react';
import { DEOnly, UKOnly } from '@iwIntlProvider/CountryWrappers';
import { Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { InterestOnlyExtend } from './InterestOnlyExtend';
import styles from './TopUpPanel.module.css';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { TopUpDrawDown } from '../../../dashboardWidgets/drawdownActionPanel/TopupDrawdown';
import { useGetDrawdownActionPanelData } from '../../../dashboardWidgets/drawdownActionPanel/useGetDrawdownActionPanelData';

export const DrawdownChoice = ({
    accountId,
    hideHeader
}: {
    accountId: string;
    hideHeader?: boolean;
}) => {
    const { topUpStatus } = useGetDrawdownActionPanelData(accountId);
    switch (topUpStatus) {
        case 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS':
            return (
                <>
                    <DEOnly>
                        {!hideHeader && (
                            <Text>
                                <FormattedMessage
                                    id="_.outstanding_view.dashboardActionPanel.extendInterestOnly"
                                    defaultMessage="You can now extend your grace period by 3 months."
                                />
                            </Text>
                        )}{' '}
                        <Text>
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.extendInterestOnlyNoChoice"
                                defaultMessage="Would you also like a credit increase?"
                            />
                        </Text>
                    </DEOnly>
                    <UKOnly>
                        <Text>
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.chooseInterestOnlyDrawdownType"
                                defaultMessage="Please select below whether you would like to extend your interest-only period, or extend and drawdown more funds"
                            />
                        </Text>
                    </UKOnly>
                    <Tab.Group as="div" className={styles.tabGroup}>
                        <Tab.List className={styles.tabList}>
                            <Tab className={styles.tab}>
                                <FormattedMessage
                                    id="_.outstanding_view.dashboardActionPanel.tabInterestOnlyWithDrawdown"
                                    defaultMessage="Extend and drawdown more funds"
                                />
                            </Tab>
                            <Tab className={styles.tab}>
                                <FormattedMessage
                                    id="_.outstanding_view.dashboardActionPanel.tabInterestOnly"
                                    defaultMessage="Only extend interest-only period"
                                />
                            </Tab>
                        </Tab.List>
                        <Tab.Panels className={styles.panels}>
                            <Tab.Panel>
                                <ErrorBoundary
                                    fallback={() => <FallbackErrorModal />}
                                >
                                    <Suspense
                                        fallback={
                                            <div
                                                className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                                data-testId="loan-overview-loading"
                                            >
                                                <MainContentPending />
                                            </div>
                                        }
                                    >
                                        <TopUpDrawDown accountId={accountId} />
                                    </Suspense>
                                </ErrorBoundary>
                            </Tab.Panel>
                            <Tab.Panel>
                                <ErrorBoundary
                                    fallback={() => <FallbackErrorModal />}
                                >
                                    <Suspense
                                        fallback={
                                            <div
                                                className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                                data-testId="loan-overview-loading"
                                            >
                                                <MainContentPending />
                                            </div>
                                        }
                                    >
                                        <InterestOnlyExtend
                                            accountId={accountId}
                                        />
                                    </Suspense>
                                </ErrorBoundary>
                            </Tab.Panel>
                        </Tab.Panels>
                    </Tab.Group>
                </>
            );
        case 'INTEREST_ONLY_EXTENSION_APPROVED':
            return (
                <div className={styles.interestOnlyContainer}>
                    {!hideHeader && (
                        <DEOnly>
                            <FormattedMessage
                                id="_.outstanding_view.dashboardActionPanel.extendInterestOnly"
                                defaultMessage="You can now extend your grace period by 3 months."
                            />
                        </DEOnly>
                    )}
                    <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                        <Suspense
                            fallback={
                                <div
                                    className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                    data-testid="loan-overview-loading"
                                >
                                    <MainContentPending />
                                </div>
                            }
                        >
                            <InterestOnlyExtend accountId={accountId} />
                        </Suspense>
                    </ErrorBoundary>
                </div>
            );
        default:
            return (
                <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                    <Suspense
                        fallback={
                            <div
                                className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                data-testid="loan-overview-loading"
                            >
                                <MainContentPending />
                            </div>
                        }
                    >
                        <TopUpDrawDown accountId={accountId} />
                    </Suspense>
                </ErrorBoundary>
            );
    }
};
